import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

const STORE_PREFIX = 'pq:';

function readPersisted(persistKey) {
  if (!persistKey) return null;
  try {
    const raw = sessionStorage.getItem(STORE_PREFIX + persistKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePersisted(persistKey, state) {
  if (!persistKey) return;
  try {
    sessionStorage.setItem(STORE_PREFIX + persistKey, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable (private mode, quota); ignore.
  }
}

/**
 * Wraps a React Query call against an endpoint that returns
 * `{ items, pagination }`. Keeps `page` and arbitrary filters in
 * component state and exposes setters that reset the page when a
 * filter changes.
 *
 * When `persistKey` is given, the current page/limit/filters are mirrored to
 * `sessionStorage` and restored on remount, so leaving the list (e.g. to edit
 * a row) and coming back keeps the same search and position within the tab
 * session.
 */
export function usePaginatedQuery({ key, fetcher, initialFilters = {}, initialLimit = 20, persistKey }) {
  const [restored] = useState(() => readPersisted(persistKey));
  const [page, setPage] = useState(() => restored?.page ?? 1);
  const [limit, setLimit] = useState(() => restored?.limit ?? initialLimit);
  const [filters, setFilters] = useState(() => restored?.filters ?? initialFilters);

  const params = { page, limit, ...filters };
  const query = useQuery({
    queryKey: [...key, params],
    queryFn: () => fetcher(params)
  });

  useEffect(() => {
    writePersisted(persistKey, { page, limit, filters });
  }, [persistKey, page, limit, filters]);

  const updateFilter = (patch) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  return {
    page,
    setPage,
    limit,
    setLimit,
    filters,
    setFilters: updateFilter,
    items: (query.data && query.data.items) || [],
    pagination: query.data && query.data.pagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch
  };
}
